import { useCallback, useEffect, useState } from "react";
import { api, type Member } from "./api";
import { errorText } from "./i18n";
import { normalizeSearch } from "./messageFilters";
import { useSession } from "./session";

/** The server's member search needs at least this many letters (PLAN.md 4.20 step 5). */
export const MIN_MEMBER_QUERY = 2;

/**
 * Member search by name, page by page (PLAN.md 4.20 step 5): null while `q` is shorter than 2
 * letters. Found members join what the page knows (session `rememberMembers`).
 */
export function useMemberSearch(q: string, limit = 20) {
  const { rememberMembers } = useSession();
  const query = normalizeSearch(q).length >= MIN_MEMBER_QUERY ? q.trim() : "";
  const [list, setList] = useState<Member[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setList(null); setNext(null); setError(null);
    if (!query) return;
    let cancelled = false;
    setLoading(true);
    // A short pause, so typing a name asks once.
    const timer = setTimeout(() => {
      api.searchMembers(query, undefined, limit).then(
        (page) => { if (cancelled) return; rememberMembers(page.members); setList(page.members); setNext(page.next_cursor); setLoading(false); },
        (err: unknown) => { if (cancelled) return; setError(errorText(err)); setLoading(false); },
      );
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, limit, rememberMembers]);
  const loadMore = useCallback(async () => {
    if (!next) return;
    setLoading(true);
    try {
      const page = await api.searchMembers(query, next, limit);
      rememberMembers(page.members);
      setList((current) => [...(current ?? []), ...page.members.filter((m) => !current?.some((c) => c.id === m.id))]);
      setNext(page.next_cursor);
    } catch (err) { setError(errorText(err)); } finally { setLoading(false); }
  }, [query, next, limit, rememberMembers]);
  return { list: query ? list : null, hasMore: next !== null, loading, error, loadMore, tooShort: !query && !!q.trim() };
}

/** Another member who owns this key (current or earlier), as far as the server tells; undefined otherwise. */
export function useKeyOwner(pubkey: string | null): Member | undefined {
  const { me, memberByKey, resolveMembers } = useSession();
  useEffect(() => { if (pubkey) resolveMembers({ keys: [pubkey] }); }, [pubkey, resolveMembers]);
  const owner = pubkey ? memberByKey(pubkey) : undefined;
  return owner && owner.id !== me.id ? owner : undefined;
}
